import { findLast, isInEnum } from "../utils/array";
import { createClientUUid } from "../utils/string";
import { formatHistory } from "./formatMessage";
import Locale from "@/app/locales";
import {
  HeaderType,
  LastHistoryMsg,
  Message,
  MessageSender,
  MessageStatus,
  MessageStore,
  MessageType,
  TemplateId,
} from "./types";

const formatSatisfyData = (msgList: Message[], historyData: Message[]) => {
  const msgListHasSatisfy = msgList.find(
    (msg) => msg.type === MessageType.MessageSatisfyResult,
  );
  if (msgListHasSatisfy) {
    return;
  }
  const matchSatisfyData = findLast(
    historyData,
    (msg) => msg.type === MessageType.MessageSatisfyResult,
  );
  if (!matchSatisfyData || matchSatisfyData?.index === -1) {
    return;
  }
  historyData[matchSatisfyData.index].disabled = true;
};

const formatSatisfyPushed = (historyData: Message[], cid?: string) => {
  const { item } = findLast(historyData, (msg) => !!msg.conversationId) || {};
  const conversationId = cid || item?.conversationId;

  // 已经评价过啦
  const hadSatisfy = historyData.find(
    (item) =>
      item.conversationId === conversationId &&
      item.type === MessageType.MessageSatisfyResult,
  );
  if (hadSatisfy) {
    return;
  }

  const matched = findLast(
    historyData,
    (item) =>
      item.type === MessageType.MessageSatisfyPushed &&
      item.conversationId === conversationId,
  );

  if (matched && matched.index !== -1) {
    historyData[matched.index].type = MessageType.Satisfy;
    historyData[matched.index].hide = false;
  }
};

const insertBeforeHistoryText = {
  headerType: HeaderType.MESSAGE,
  clientId: createClientUUid(MessageType.Template),
  type: MessageType.Template,
  templateId: TemplateId.History,
  content: `{"text": "${Locale.chat.wholeHistory}"}`,
  senderType: MessageSender.SYSTEM,
  timestamp: Date.now(),
  status: MessageStatus.Success,
};

export const historyMsgManager = ({
  set,
  get,
}: {
  set: (
    partial:
      | MessageStore
      | Partial<MessageStore>
      | ((state: MessageStore) => MessageStore | Partial<MessageStore>),
    replace?: false | undefined,
  ) => void;
  get: () => MessageStore;
}) => {
  const getFormatData = (messages: Message[]) => {
    const historyData = messages.map((msg) => {
      // 不在消息类型内的消息，一律不能展示
      const msgNeedHide =
        !isInEnum(msg.type, MessageType) ||
        !!msg.hide ||
        msg.type === MessageType.MessageSatisfyPushed;
      return {
        ...msg,
        hide: msgNeedHide,
        status: MessageStatus.Success, // 历史消息都是成功状态
      };
    });
    // formatHistory
    return formatHistory(historyData);
  };

  const handleHistoryInsert = (
    messages: Message[],
    isInitializeHistory = false,
  ) => {
    const formatData = getFormatData(messages);
    let combineHistoryMsgList = [...formatData];
    if (!isInitializeHistory) {
      // 如果不是初始化，插入消息需要补充
      combineHistoryMsgList = get().historyMsgList;
      combineHistoryMsgList.splice(1, 0, ...formatData);
    }
    const { sessionInfo } = get();
    // 1. 如果是初始化历史消息，判断会话状态，如果不在会话中，则插入“以上是历史记录”
    let historyData = [...combineHistoryMsgList];
    if (isInitializeHistory) {
      // 2. 如果是初始化历史消息，判断会话状态，如果在会话中, 往当前历史记录中找不一样的会话ID，若能找到，则插入
      if (sessionInfo?.conversationId) {
        // 存在会话ID
        const matchNotSameMsg = findLast(
          combineHistoryMsgList,
          (item) => item.conversationId !== sessionInfo.conversationId,
        );
        const index = matchNotSameMsg?.index;
        if (index && index >= 0) {
          historyData.splice(index, 0, insertBeforeHistoryText);
        }
      } else {
        historyData = [...combineHistoryMsgList, insertBeforeHistoryText];
      }
    }
    formatSatisfyData(get().msgList, historyData);
    formatSatisfyPushed(historyData, sessionInfo?.conversationId);
    set({
      historyMsgList: historyData,
    });
  };
  return {
    lastHistoryMsg: {
      nextCursor: "",
      hasMore: true,
    },
    historyMsgList: [],
    historyLoaded: false,
    setLastHistoryMsg: (data: LastHistoryMsg) => {
      set(() => {
        return {
          lastHistoryMsg: data,
        };
      });
    },
    insertHistory: (messages: Message[]) => {
      handleHistoryInsert(messages);
    },
    // 初始化历史数据
    initializeHistory: (messages: Message[]) => {
      handleHistoryInsert(
        [
          {
            headerType: HeaderType.MESSAGE,
            clientId: createClientUUid(MessageType.Template),
            type: MessageType.Template,
            templateId: TemplateId.More_History,
            content: "",
            senderType: MessageSender.SYSTEM,
            timestamp: Date.now(),
            status: MessageStatus.Success,
          },
          ...messages,
        ],
        true,
      );
      // 标记历史记录已加载完成
      set({ historyLoaded: true });

      set({
        msgList: [],
      });
    },
  };
};
